import "./PasswordReset.css";

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import axios from "axios";
import jwt_decode from "jwt-decode";
import { useSignIn } from "react-auth-kit";
import logo from "../abe_logo.png";
import { PAGES } from "../constants";

function PasswordReset() {
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const signIn = useSignIn();

  const queryParams = new URLSearchParams(search);
  const defaultEmail = state?.email || queryParams.get("email");
  const defaultPasswordResetCode = queryParams.get("password_reset_code");

  const [email, setEmail] = useState("");
  const [passwordResetCode, setPasswordResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [verificationError, setVerificationError] = useState("");

  const emailRef = useRef(null);
  const passwordResetCodeRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmNewPasswordRef = useRef(null);

  const setInputs = () => {
    setEmail(emailRef.current.value);
    setPasswordResetCode(passwordResetCodeRef.current.value);
    setNewPassword(newPasswordRef.current.value);
    setConfirmNewPassword(confirmNewPasswordRef.current.value);
  }

  useEffect(() => {
    setInputs();
  }, []);

  const handlePasswordReset = (event, email, passwordResetCode) => {
    if (event) {
      event.preventDefault();
    }

    if (newPassword !== confirmNewPassword) {
      setVerificationError("Passwords do not match");
      return;
    }

    const passwordResetUrl = `${process.env.REACT_APP_BASE_URL}/user/password_reset/verify`;
    const passwordResetData = {
      email: email,
      password_reset_code: passwordResetCode,
      new_password: newPassword,
    };
    const passwordResetConfig = {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    };

    axios.post(passwordResetUrl, passwordResetData, passwordResetConfig)
      .then(response => {
        const jwtDecoded = jwt_decode(response.data.access_token);
        const expiryEpoch = jwtDecoded.exp; // Expiry epoch in seconds
        const currentEpoch = (new Date()).getTime() / 1000; // Current epoch in seconds
        const expiresIn = Math.ceil((expiryEpoch - currentEpoch) / 60) // Amount of minutes until expiry
        if (signIn(
          {
            token: response.data.access_token,
            expiresIn: Math.max(expiresIn, 0), // Ensure expiresIn is not negative
            tokenType: response.data.token_type,
            authState: { email: jwtDecoded.sub }
          }
        )) {
          navigate(PAGES.HOME);
        }
      })
      .catch(error => {
        if (error.response) {
          if (typeof error.response.data.detail === "string" && error.response.data.detail !== "") {
            setVerificationError(error.response.data.detail);
          } else {
            setVerificationError("Something went wrong, please ensure all fields are filled out correctly");
          }
        } else {
          setVerificationError("Unable to connect to server");
        }
      });
  }

  const handleSendPasswordResetEmail = () => {
    const searchParams = new URLSearchParams({
      email: email
    });
    const passwordResetUrl = `${process.env.REACT_APP_BASE_URL}/user/password_reset/request?${searchParams.toString()}`;
    const passwordResetData = {};
    const passwordResetConfig = {
      headers: { "accept": "application/json" },
    };

    axios.post(passwordResetUrl, passwordResetData, passwordResetConfig)
      .then(response => {
        setVerificationError(response.data.message);
      })
      .catch(error => {
        if (error.response) {
          if (typeof error.response.data.detail === "string") {
            setVerificationError(error.response.data.detail);
          } else if (Array.isArray(error.response.data.detail)) {
            error.response.data.detail.forEach(detail => {
              if (detail.loc[1] === "email") {
                setVerificationError(detail.msg);
              }
            });
          } else {
            setVerificationError("Something went wrong, please try again later");
          }
        } else {
          setVerificationError("Unable to connect to server");
        }
      });
  }

  return (
    <div className="login-container" data-testid="password-reset-page-div">
      <img src={logo} className="logo" alt="logo" />
      <p className="password-reset-info-text">Reset your password</p>

      <form className="password-reset-form" onSubmit={(event) => handlePasswordReset(event, email, passwordResetCode)}>
        <input className="auth-input" type="email" placeholder="Email" ref={emailRef} onChange={setInputs} defaultValue={defaultEmail} />
        <input className="auth-input" type="text" placeholder="Password Reset Code" ref={passwordResetCodeRef} onChange={setInputs} defaultValue={defaultPasswordResetCode} />
        <input className="auth-input" type="password" placeholder="New Password" ref={newPasswordRef} onChange={setInputs} />
        <input className="auth-input" type="password" placeholder="Confirm New Password" ref={confirmNewPasswordRef} onChange={setInputs} />

        <div className="auth-center-contents">
          {verificationError !== "" && <p className="error-message">{verificationError}</p>}
          <input type="submit" className="auth-button" value="Reset Password" />
        </div>
      </form>

      <button className="authentication-form-hyperlink" onClick={handleSendPasswordResetEmail}>Send password reset email</button>
    </div>
  );
}

export default PasswordReset;